import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NotificationCard from '../components/shared/NotificationCard';
import { NotificationType } from '../types';

const Notifications: React.FC = () => {
  const { 
    notifications, 
    unreadNotificationCount, 
    fetchNotifications, 
    markAllNotificationsAsRead,
    isLoading 
  } = useData();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread' | NotificationType>('all');

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNavigate = (url: string) => {
    navigate(url);
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });

  const filterOptions = [
    { value: 'all', label: t('notifications.filters.all') },
    { value: 'unread', label: t('notifications.filters.unread') },
    { value: 'new_release_pending', label: t('notifications.filters.newReleasePending') },
    { value: 'release_approved', label: t('notifications.filters.releaseApproved') },
    { value: 'release_rejected', label: t('notifications.filters.releaseRejected') },
    { value: 'payment_sent', label: t('notifications.filters.paymentSent') },
    { value: 'release_published', label: t('notifications.filters.releasePublished') },
    { value: 'new_artist_registered', label: t('notifications.filters.newArtistRegistered') },
    { value: 'system_update', label: t('notifications.filters.systemUpdate') }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('notifications.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('notifications.description')} 
            {unreadNotificationCount > 0 && (
              <span className="ml-2 bg-orange-500/20 text-orange-300 text-xs px-2 py-1 rounded-full border border-orange-500/30">
                {unreadNotificationCount} {t('notifications.unread')}
              </span>
            )}
          </p>
        </div>
        
        {unreadNotificationCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-300 transform hover:scale-105"
          >
            {t('notifications.markAllAsRead')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value as typeof filter)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filter === option.value
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-medium text-white mb-2">
              {filter === 'unread' 
                ? t('notifications.noUnreadNotifications') 
                : t('notifications.noNotifications')
              }
            </h3>
            <p className="text-white/70">
              {filter === 'unread' 
                ? t('notifications.noUnreadNotificationsDescription') 
                : t('notifications.noNotificationsDescription')
              }
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationCard 
              key={notification.id} 
              notification={notification} 
              onNavigate={handleNavigate}
            />
          ))
        )}
      </div>

      {/* Summary */}
      {notifications.length > 0 && (
        <div className="mt-8 p-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm text-white/70">
                {t('notifications.totalNotifications', { count: notifications.length })}
              </span>
            </div>
            <div className="text-right">
              <span className="text-sm text-white/70">
                {t('notifications.unreadCount', { count: unreadNotificationCount })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications; 